import { useEffect, useMemo, useState } from "react";
import { Phone, Mail, Search, Loader2, RefreshCw, AlarmClock, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api, errText, STAGES, stageLabel, money, shortDate } from "./api";

const STAGE_STYLE = {
  new: "bg-blue-100 text-blue-800", contacted: "bg-slate-200 text-slate-800", inspected: "bg-violet-100 text-violet-800",
  estimate_sent: "bg-orange-100 text-orange-800", won: "bg-green-100 text-green-800", lost: "bg-red-100 text-red-700",
};
export const StagePill = ({ stage }) => <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STAGE_STYLE[stage] ?? STAGE_STYLE.new}`}>{stageLabel(stage)}</span>;

const CallbackTag = ({ l }) => {
  if (!l.callback_date || !["new", "contacted", "inspected", "estimate_sent"].includes(l.stage)) return null;
  const d = new Date(`${l.callback_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const cls = l.overdue ? "bg-red-600 text-white" : l.due_today ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-700";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${cls}`} data-testid={`lead-callback-${l.id}`}><AlarmClock className="h-3.5 w-3.5" />{l.overdue ? `Overdue · ${d}` : l.due_today ? "Call today" : `Call ${d}`}</span>;
};

const LeadRow = ({ l, onOpen }) => (
  <button onClick={() => onOpen(l)} className={`grid w-full gap-1 rounded-xl border bg-white p-4 text-left transition-shadow hover:shadow-md md:grid-cols-[1.3fr_1fr_1fr_auto] md:items-center md:gap-4 ${l.overdue ? "border-red-500 ring-1 ring-red-500" : "border-slate-200"}`} data-testid={`lead-row-${l.id}`}>
    <span className="min-w-0"><span className="block truncate font-bold">{l.name}</span><span className="text-sm text-slate-500">{l.phone}</span></span>
    <span className="text-sm text-slate-700">{l.service}{l.project_type ? ` · ${l.project_type}` : ""}{l.notes?.length ? <span className="ml-2 inline-flex items-center gap-1 text-xs text-slate-500"><StickyNote className="h-3 w-3" />{l.notes.length}</span> : null}</span>
    <span className="text-sm text-slate-500">{l.town} · {shortDate(l.created_at)}</span>
    <span className="flex flex-wrap items-center gap-2 md:justify-end"><CallbackTag l={l} /><span className="text-sm font-semibold">{money(l.amount)}</span><StagePill stage={l.stage} /></span>
  </button>
);

const Detail = ({ k, v }) => (v ? <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm"><span className="text-slate-500">{k}</span><span className="text-right font-medium">{v}</span></div> : null);

const Notes = ({ lead, onSaved }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/leads/${lead.id}/notes`, { text }); setText(""); onSaved(data); toast.success("Note added"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const notes = [...(lead.notes ?? [])].reverse();
  return (
    <div className="mt-6" data-testid="lead-notes">
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Call notes</p>
      <textarea rows={3} className="inp mt-2" placeholder="e.g. Left voicemail. Wants a quote for full tear-off, home after 5pm." value={text} onChange={(e) => setText(e.target.value)} data-testid="lead-note-input" />
      <button onClick={add} disabled={busy || !text.trim()} className="mt-2 flex items-center gap-2 rounded-full border-2 border-slate-900 px-4 py-2 text-sm font-bold disabled:opacity-50" data-testid="lead-note-add-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Add note</button>
      <ul className="mt-3 space-y-2">
        {notes.map((n) => (
          <li key={n.id} className="rounded-lg bg-slate-50 p-3 text-sm" data-testid={`lead-note-${n.id}`}>
            <p className="whitespace-pre-wrap">{n.text}</p>
            <p className="mt-1 text-xs text-slate-500">{n.by} · {new Date(n.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const LeadSheet = ({ lead, onClose, onSaved }) => {
  const [stage, setStage] = useState(lead?.stage ?? "new");
  const [amount, setAmount] = useState(lead?.amount ?? "");
  const [callback, setCallback] = useState(lead?.callback_date ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStage(lead?.stage ?? "new"); setAmount(lead?.amount ?? ""); setCallback(lead?.callback_date ?? ""); }, [lead]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { stage, amount: amount === "" ? null : Number(amount), callback_date: callback || null });
      onSaved(data); toast.success("Lead updated");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="lead-sheet">
        {lead && <>
          <SheetHeader><SheetTitle className="font-display text-3xl font-black uppercase">{lead.name}</SheetTitle><SheetDescription>{lead.service} lead from {lead.town}</SheetDescription></SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <a href={`tel:${lead.phone}`} className="flex items-center justify-center gap-2 rounded-full bg-slate-900 py-3 text-sm font-bold text-white" data-testid="lead-call-button"><Phone className="h-4 w-4" /> Call</a>
            {lead.email ? <a href={`mailto:${lead.email}`} className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 py-3 text-sm font-bold" data-testid="lead-email-button"><Mail className="h-4 w-4" /> Email</a>
              : <a href={`sms:${lead.phone}`} className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 py-3 text-sm font-bold" data-testid="lead-text-button">Text</a>}
          </div>
          <p className="mt-6 text-xs font-bold uppercase tracking-widest text-slate-500">Sales stage</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {STAGES.map((s) => (
              <button key={s.id} onClick={() => setStage(s.id)} data-testid={`lead-stage-${s.id}`}
                className={`rounded-full border px-3 py-2 text-sm font-semibold ${stage === s.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700"}`}>{s.label}</button>
            ))}
          </div>
          <label className="mt-5 block text-xs font-bold uppercase tracking-widest text-slate-500">Job amount ($)
            <input type="number" min="0" inputMode="decimal" className="inp mt-2" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Estimate or final price" data-testid="lead-amount-input" />
          </label>
          <label className="mt-4 block text-xs font-bold uppercase tracking-widest text-slate-500">Call back on
            <div className="mt-2 flex gap-2">
              <input type="date" className="inp" value={callback} onChange={(e) => setCallback(e.target.value)} data-testid="lead-callback-input" />
              {callback && <button type="button" onClick={() => setCallback("")} className="shrink-0 rounded-full border border-slate-300 px-3 text-xs font-semibold" data-testid="lead-callback-clear">Clear</button>}
            </div>
          </label>
          <button onClick={save} disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-3 font-bold text-white disabled:opacity-60" data-testid="lead-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
          <Notes lead={lead} onSaved={onSaved} />
          <div className="mt-6">
            <Detail k="Phone" v={lead.phone} /><Detail k="Email" v={lead.email} />
            <Detail k="Address" v={lead.address} /><Detail k="Town" v={lead.town} />
            <Detail k="Service" v={lead.service} /><Detail k="Project" v={lead.project_type} />
            <Detail k="Timeline" v={lead.timeline} /><Detail k="Homeowner" v={lead.homeowner} />
            <Detail k="Came from page" v={lead.page} /><Detail k="Received" v={shortDate(lead.created_at)} />
            <Detail k="Email alert" v={{ sent: "Emailed to you", failed: "Email failed", no_owner_email: "Not emailed (no address set then)" }[lead.email_status]} />
            <Detail k="Thank-you email" v={{ sent: "Sent to customer", failed: "Failed to send" }[lead.thanks_status]} />
            <Detail k="Last updated by" v={lead.updated_by} />
            {lead.message && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">{lead.message}</p>}
          </div>
        </>}
      </SheetContent>
    </Sheet>
  );
};

export default function Leads() {
  const [leads, setLeads] = useState(null);
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/leads").then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errText(e)); setLeads([]); });
  useEffect(() => { load(); }, []);
  const counts = useMemo(() => (leads ?? []).reduce((a, l) => ({ ...a, [l.stage]: (a[l.stage] ?? 0) + 1, due: (a.due ?? 0) + (l.overdue || l.due_today ? 1 : 0), overdue: (a.overdue ?? 0) + (l.overdue ? 1 : 0) }), {}), [leads]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = (leads ?? []).filter((l) => (stage === "all" || (stage === "due" ? l.overdue || l.due_today : l.stage === stage)) &&
      (!t || [l.name, l.phone, l.email, l.address, l.town].some((v) => (v ?? "").toLowerCase().includes(t))));
    return stage === "due" ? list.sort((a, b) => (a.callback_date ?? "").localeCompare(b.callback_date ?? "")) : list;
  }, [leads, stage, q]);
  const saved = (l) => { setLeads((xs) => xs.map((x) => (x.id === l.id ? l : x))); setOpen(l); };
  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-4xl font-black uppercase">Leads</h1>
        <button onClick={load} className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold" data-testid="leads-refresh-button"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      {counts.overdue > 0 && (
        <button onClick={() => setStage("due")} className="mt-4 flex w-full items-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-left text-sm font-bold text-white" data-testid="leads-overdue-banner">
          <AlarmClock className="h-5 w-5 shrink-0" />{counts.overdue} overdue callback{counts.overdue === 1 ? "" : "s"}. Tap to see who to call.
        </button>
      )}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {[{ id: "all", label: "All" }, { id: "due", label: "Callbacks due" }, ...STAGES].map((s) => (
          <button key={s.id} onClick={() => setStage(s.id)} data-testid={`leads-filter-${s.id}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${stage === s.id ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>
            {s.label} <span className="opacity-70">{s.id === "all" ? leads?.length ?? 0 : counts[s.id] ?? 0}</span>
          </button>
        ))}
      </div>
      <label className="relative mt-3 block">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input className="inp !pl-10" placeholder="Search name, phone, email, address or town" value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-search-input" />
      </label>
      <div className="mt-4 grid gap-2">
        {leads === null && <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-slate-400" />}
        {leads !== null && shown.length === 0 && <p className="rounded-xl bg-white p-8 text-center text-slate-500" data-testid="leads-empty">No leads here yet. Every quote request from the website lands here automatically.</p>}
        {shown.map((l) => <LeadRow key={l.id} l={l} onOpen={setOpen} />)}
      </div>
      <LeadSheet lead={open} onClose={() => setOpen(null)} onSaved={saved} />
    </div>
  );
}
